/**
 * InformationsFormDataService - Clean Signal-Based Data Loading
 * 
 * Simplified data loading service using pure Angular signals:
 * - Clean separation of concerns: only handles data loading
 * - Signal-based reactive state management
 * - Proper async handling with loading states
 * - No complex cascading logic (handled by component)
 * - Memory efficient and performant
 * 
 * @author Claude Code
 * @version 8.0 - Clean Signal Architecture
 */
import { Injectable, inject, signal, computed } from '@angular/core';
import { ReferentielsService } from '../../../core/services/referentiels.service';
import { DirectionService } from '../../../core/services/direction.service';
import { FonctionnelService } from '../../../core/services/fonctionnel.service';
import { AgentsService } from '../../../core/services/agents.service';
import {
  RecruitmentDirection as Direction,
  RecruitmentServiceFonctionnel as ServiceFonctionnel
} from '../../../core/models/domains/recruitment';
import { Pole } from '../../../core/models/shared';
import { Poste } from '../../../core/models/domains/posts';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InformationsFormDataService {
  private readonly referentielsService = inject(ReferentielsService);
  private readonly directionService = inject(DirectionService);
  private readonly fonctionnelService = inject(FonctionnelService);
  private readonly agentsService = inject(AgentsService);

  // =================================================================
  // DATA SIGNALS - Simple state management
  // =================================================================
  
  // Static data lists
  readonly typeRecrutementList = signal<any[]>([]);
  readonly poleList = signal<Pole[]>([]);
  readonly motifLiberationList = signal<any[]>([]);
  
  // Dynamic data lists
  readonly directionList = signal<Direction[]>([]);
  readonly serviceFonctionnelList = signal<ServiceFonctionnel[]>([]);
  readonly posteList = signal<Poste[]>([]);
  
  // Loading states
  readonly isLoadingStatic = signal(false);
  readonly isLoadingDirections = signal(false);
  readonly isLoadingServices = signal(false);
  readonly isLoadingPostes = signal(false);
  
  // Error states
  readonly staticError = signal<string | null>(null);
  readonly directionsError = signal<string | null>(null);
  readonly servicesError = signal<string | null>(null);
  readonly postesError = signal<string | null>(null);

  // =================================================================
  // COMPUTED STATE - Derived values
  // =================================================================
  
  readonly isLoading = computed(() => 
    this.isLoadingStatic() || this.isLoadingDirections() || 
    this.isLoadingServices() || this.isLoadingPostes()
  );
  
  readonly hasError = computed(() => 
    Boolean(this.staticError() || this.directionsError() || 
            this.servicesError() || this.postesError())
  );
  
  readonly errorMessage = computed(() => {
    return this.staticError() || this.directionsError() || 
           this.servicesError() || this.postesError() || null;
  });

  readonly isDataReady = computed(() => 
    !this.isLoading() && !this.hasError() && this.poleList().length > 0
  );

  // =================================================================
  // PUBLIC API - Simple async data loading methods
  // =================================================================
  
  /**
   * Load all static data (called once on init)
   */
  async loadStaticData(): Promise<void> {
    this.isLoadingStatic.set(true);
    this.staticError.set(null);
    
    try {
      const [typeRecrutement, poles, motifLiberation] = await Promise.all([
        lastValueFrom(this.referentielsService.getTypeRecrutement()),
        lastValueFrom(this.referentielsService.getPole()),
        lastValueFrom(this.referentielsService.getMotifDepart())
      ]);
      
      this.typeRecrutementList.set(typeRecrutement || []);
      this.poleList.set(poles || []);
      this.motifLiberationList.set(motifLiberation || []);
      
    } catch (error) {
      console.error('Error loading static data:', error);
      this.staticError.set('Erreur lors du chargement des données de base');
    } finally {
      this.isLoadingStatic.set(false);
    }
  }

  /**
   * Load directions for a specific pole
   */
  async loadDirections(poleId: string): Promise<Direction[]> {
    this.isLoadingDirections.set(true);
    this.directionsError.set(null);
    
    try {
      const directions = await lastValueFrom(this.directionService.getMyDirectionsByPole(poleId));
      const directionList = directions || [];
      this.directionList.set(directionList);
      return directionList;
      
    } catch (error) {
      console.error('Error loading directions:', error);
      this.directionsError.set('Erreur lors du chargement des directions');
      this.directionList.set([]);
      return [];
    } finally {
      this.isLoadingDirections.set(false);
    }
  }

  /**
   * Load services for a specific direction
   */
  async loadServices(directionId: string): Promise<ServiceFonctionnel[]> {
    this.isLoadingServices.set(true);
    this.servicesError.set(null);
    
    try {
      const services = await lastValueFrom(this.fonctionnelService.getMyServicesByDirection(directionId));
      const serviceList = services || [];
      this.serviceFonctionnelList.set(serviceList);
      return serviceList;
      
    } catch (error) {
      console.error('Error loading services:', error);
      this.servicesError.set('Erreur lors du chargement des services');
      this.serviceFonctionnelList.set([]);
      return [];
    } finally {
      this.isLoadingServices.set(false);
    }
  }

  /**
   * Load postes for direction and optional service
   */
  async loadPostes(directionId: string | number, serviceId?: string | number): Promise<Poste[]> {
    this.isLoadingPostes.set(true);
    this.postesError.set(null);
    
    try {
      const postes = await lastValueFrom(this.referentielsService.getAllPostesOpened(
        String(directionId), 
        serviceId ? String(serviceId) : undefined
      ));
      const posteList = postes || [];
      this.posteList.set(posteList);
      return posteList;
      
    } catch (error) {
      console.error('Error loading postes:', error);
      const errorMessage = (error as any)?.error || 'Erreur lors du chargement des postes';
      this.postesError.set(errorMessage);
      this.posteList.set([]);
      return [];
    } finally {
      this.isLoadingPostes.set(false);
    }
  }

  /**
   * Get current user's pole
   */
  async getCurrentUserPole(): Promise<Pole | null> {
    try {
      const agent = await lastValueFrom(this.agentsService.getCurrentAgent());
      if (agent?.libellePole) {
        const poles = this.poleList();
        return poles.find(pole => pole.libelle === agent.libellePole) || null;
      }
      return null;
    } catch (error) {
      console.error('Error loading current agent:', error);
      return null;
    }
  }

  /**
   * Clear dependent data when selections change
   */
  clearDirections(): void {
    this.directionList.set([]);
    this.directionsError.set(null);
  }

  clearServices(): void {
    this.serviceFonctionnelList.set([]);
    this.servicesError.set(null);
  }

  clearPostes(): void {
    this.posteList.set([]);
    this.postesError.set(null);
  }

  /**
   * Reset all data to initial state
   */
  reset(): void {
    this.clearDirections();
    this.clearServices();
    this.clearPostes();
  }
}